/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Form, Input, Modal, message, Typography } from 'antd';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getProfileUser, watchAuthLoading } from '@/redux/slices/auth.slice';
import { Button } from '@/components/button';

import { ApiFetcher } from '@/utils/api-service';

type LocalTestLoginModalProps = {
  open: boolean;
};

type FieldType = {
  access_token: string;
  refresh_token: string;
};

const sanitizeToken = (token: string) => {
  if (!token) return '';
  let clean = token.trim();
  if (
    (clean.startsWith('"') && clean.endsWith('"')) ||
    (clean.startsWith("'") && clean.endsWith("'"))
  ) {
    clean = clean.slice(1, -1).trim();
  }
  if (clean.toLowerCase().startsWith('bearer ')) {
    clean = clean.slice(7).trim();
  }
  return clean;
};

export const LocalTestLoginModal = ({ open }: LocalTestLoginModalProps) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(watchAuthLoading);

  const onFinish = async (values: FieldType) => {
    const cleanAccessToken = sanitizeToken(values.access_token);
    const cleanRefreshToken = sanitizeToken(values.refresh_token);

    if (ApiFetcher.isTokenExpired(cleanAccessToken)) {
      message.warning(
        'Access Token có thể đã hết hạn hoặc sai định dạng JWT. Thử đăng nhập, nếu thất bại hãy lấy Token mới.'
      );
    }

    try {
      localStorage.setItem('access_token', cleanAccessToken);
      localStorage.setItem('refresh_token', cleanRefreshToken);

      await dispatch(getProfileUser()).unwrap();
      message.success('Đăng nhập thành công!');
    } catch {
      message.error('Token không hợp lệ hoặc không tìm thấy user trên server!');
    }
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={520}
      title={
        <Typography.Title level={4} css={titleStyle}>
          Local Test - Nhập Token Đăng Nhập
        </Typography.Title>
      }
    >
      <div css={descriptionStyle}>
        Môi trường hiện tại đang bật <code>LOCAL_TEST=true</code>. Vui lòng nhập
        Access Token và Refresh Token để đăng nhập.
      </div>
      <Form form={form} layout='vertical' onFinish={onFinish} css={formStyle}>
        <Form.Item
          label='Access Token'
          name='access_token'
          rules={[
            {
              required: true,
              message: 'Vui lòng nhập Access Token'
            }
          ]}
        >
          <Input.TextArea rows={3} placeholder='Nhập Access Token...' />
        </Form.Item>

        <Form.Item
          label='Refresh Token'
          name='refresh_token'
          rules={[
            {
              required: true,
              message: 'Vui lòng nhập Refresh Token'
            }
          ]}
        >
          <Input.TextArea rows={3} placeholder='Nhập Refresh Token...' />
        </Form.Item>

        <Form.Item css={buttonItemStyle}>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            block
            shape='round'
            size='large'
          >
            Đăng nhập
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const titleStyle = css`
  margin: 0 !important;
  font-size: 2rem !important;
`;

const descriptionStyle = css`
  margin-top: 0.8rem;
  font-size: 1.4rem;
  color: rgba(0, 0, 0, 0.65);

  code {
    background-color: #f5f5f5;
    padding: 0.2rem 0.4rem;
    border-radius: 0.4rem;
    color: #d9363e;
    font-weight: 600;
  }
`;

const formStyle = css`
  margin-top: 1.6rem;

  .ant-form-item-label > label {
    font-weight: 500;
  }
`;

const buttonItemStyle = css`
  margin-bottom: 0 !important;
  margin-top: 2rem;
`;
